import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { NewsArticle } from "@/data/newsData";
import { trackEvent } from "@/lib/analytics";

interface NewsCardProps {
  article: NewsArticle;
  showExcerpt?: boolean;
}

const NewsCard = ({ article, showExcerpt = true }: NewsCardProps) => {
  return (
    <Card className="group overflow-hidden hover:scale-[1.02] hover:shadow-lg transition-all duration-300 flex flex-col h-full">
      {/* Image or Gradient Placeholder */}
      {article.image ? (
        <img 
          src={article.image}
          alt={article.title}
          className="h-48 w-full object-cover"
          loading="lazy"
          width="400"
          height="192"
        />
      ) : (
        <div 
          className="h-48 w-full"
          style={{
            background: 'linear-gradient(135deg, hsl(192 100% 50% / 0.3) 0%, hsl(220 100% 60% / 0.3) 100%)'
          }}
        />
      )}
      
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">
            {article.category}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {format(article.date, "MMMM d, yyyy")}
          </span>
        </div>
        <CardTitle className="text-xl md:text-2xl font-bold group-hover:text-primary transition-colors">
          {article.title}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex flex-col flex-1">
        {showExcerpt && (
          <>
            <CardDescription className="text-sm md:text-base mb-4">
              {article.excerpt}
            </CardDescription>
            
            {article.author && (
              <p className="text-xs text-muted-foreground mb-4">
                By {article.author}
              </p>
            )}
          </>
        )}
        
        <Button variant="secondary" size="sm" asChild className="w-full mt-auto">
          <a
            href={article.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2"
            onClick={() =>
              trackEvent("news_article_click", {
                article_id: article.id,
                article_title: article.title,
                article_category: article.category,
                outbound_url: article.externalLink,
              })
            }
          >
            Read article: {article.category}
            <ExternalLink className="w-4 h-4" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};

export default NewsCard;